import React, {useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {configActions, selectActiveTool, selectEditorLockState} from "../../config/configSlice";
import {topViewWallAreaActions, selectActiveTopViewWallAreaId} from "../../topViewWallArea/topViewWallAreaSlice";
import {TopViewWallAreaMeasurement, AreaStyle, WallDeduction} from "../../../models/editor";
import {TopViewWallAreaHelper} from "../../topViewWallArea/TopViewWallAreaHelper";
import {EditorTool, MaterialType, MeasurementType} from "../../../models/enums";
import {useHighlightItem, useHighlightSetter} from "../../../hooks/highlight";
import {useUserSettingsDataContext} from "../../../../../../../providers/UserSettingsProvider";
import {formatLength} from "../../../../../../../utils/TextUtils";
import {useMaterialModalContext} from "../material/MaterialModalProvider";
import {useEditorPanelDataContext} from "../EditorPanelProvider";
import MeasurementStyleSelectorModal from "../../../components/MeasurementStyleSelectorModal";
import ConfirmationModal from "../../../../../../../components/ui/ConfirmationModal";
import SetWallHeightsModal from "./SetWallHeightsModal";
import CreateWallDeductionModal from "./CreateWallDeductionModal";
import WallHeightPointItem from "./WallHeightPointItem";
import {WallDeductionItem} from "./WallDeductionItem";
import Dropdown from "../../../../../../../components/ui/Dropdown";
import DropdownItem from "../../../../../../../components/ui/DropdownItem";
import IconArrowSmall from "../../../icons/IconArrowSmall";
import IconVisibleButton from "../../../components/IconVisibleButton";
import {IconPin} from "../../../icons/side_panel/IconPin";
import {getId} from "../../../../../../../utils";
import {selectScale} from "../../scale/scaleSlice";
import NotificationService from "../../../../../../../services/NotificationService";

// Reuse length panel item styles
import "../length/lengthPanelItem.scss";

enum PanelItemModal {
	NONE = "NONE",
	STYLE = "STYLE",
	DELETE = "DELETE",
	HEIGHTS = "HEIGHTS",
	DEDUCTION = "DEDUCTION",
}

interface Props {
	measurement: TopViewWallAreaMeasurement;
}

const TopViewWallAreaPanelItem: React.FC<Props> = ({measurement}) => {
	const dispatch = useDispatch();
	const {t} = useTranslation();
	const scale = useSelector(selectScale);
	const editorLocked = useSelector(selectEditorLockState);
	const activeTool = useSelector(selectActiveTool);
	const activeId = useSelector(selectActiveTopViewWallAreaId);
	const isActive = activeId === measurement.id && activeTool === EditorTool.TOP_VIEW_WALL_AREA;
	const {settings} = useUserSettingsDataContext();
	const {openMaterialChangeModal, openOneOffChangeModal} = useMaterialModalContext();
	const {pinnedItems, findPinnedMeasurement} = useEditorPanelDataContext();

	const [isExpanded, setIsExpanded] = useState(false);
	const [modal, setModal] = useState<PanelItemModal>(PanelItemModal.NONE);
	const [editingDeduction, setEditingDeduction] = useState<WallDeduction | null>(null);
	const highlightPanelActions = useHighlightSetter(measurement.id);
	const {isItemHover} = useHighlightItem(measurement.id);
	const isOneOff = measurement.material.type === MaterialType.ONE_OFF;
	const pinnedMeasurement = findPinnedMeasurement(measurement);

	const formattedValue = TopViewWallAreaHelper.getFormattedValue(measurement, scale, settings?.measurementSystem);
	const orderedPoints = TopViewWallAreaHelper.getOrderedUniquePoints(measurement);

	const openStyleModal = () => setModal(PanelItemModal.STYLE);
	const openDeleteModal = () => setModal(PanelItemModal.DELETE);
	const openHeightsModal = () => setModal(PanelItemModal.HEIGHTS);
	const closeModal = () => setModal(PanelItemModal.NONE);

	const openChangeMaterialModal = () => openMaterialChangeModal({
		measurementId: measurement.id,
		measurementStyle: measurement.style,
		measurementType: EditorTool.TOP_VIEW_WALL_AREA,
	});

	const openChangeOneOffModal = (modalTitle: string) => () => openOneOffChangeModal({
		measurementId: measurement.id,
		measurementStyle: measurement.style,
		measurementType: EditorTool.TOP_VIEW_WALL_AREA,
		materialName: measurement.material.name,
		modalTitle,
	});

	function handleActivate() {
		const activationId = getId();
		dispatch(configActions.switchEditorTool({editorTool: EditorTool.TOP_VIEW_WALL_AREA, actionId: activationId}));
		dispatch(topViewWallAreaActions.activate({id: measurement.id, actionId: activationId}));
		dispatch(topViewWallAreaActions.setVisibility({id: measurement.id, visible: true, actionId: activationId}));
	}

	function handleDeactivate() {
		dispatch(topViewWallAreaActions.clearActive({}));
	}

	function toggleActivate() {
		isActive ? handleDeactivate() : handleActivate();
	}

	function handleStyleSave(style: AreaStyle) {
		closeModal();
		dispatch(topViewWallAreaActions.changeStyle({id: measurement.id, style}));
	}

	function handleDelete() {
		dispatch(topViewWallAreaActions.delete({id: measurement.id}));
	}

	function toggleVisibility() {
		dispatch(topViewWallAreaActions.setVisibility({id: measurement.id, visible: !measurement.visible}));
	}

	function createDeduction() {
		setEditingDeduction(null);
		setIsExpanded(true);
		setModal(PanelItemModal.DEDUCTION);
	}

	function handleSaveDeduction(deduction: Omit<WallDeduction, 'id'>) {
		const grossArea = TopViewWallAreaHelper.calculateGrossArea(measurement, scale);
		const isManualAreaDeduction =
			deduction.leftHeight === 0 &&
			deduction.rightHeight === 0 &&
			deduction.topWidth === 0 &&
			deduction.bottomWidth === 0 &&
			deduction.area > 0;
		const effectiveDeductionArea = isManualAreaDeduction
			? deduction.area
			: TopViewWallAreaHelper.calculateDeductionArea(deduction);
		const otherDeductionsTotal = measurement.wallDeductions
			.filter(d => d.id !== editingDeduction?.id)
			.reduce((sum, d) => sum + d.area, 0);
		if (effectiveDeductionArea > grossArea - otherDeductionsTotal) {
			NotificationService.errorNotification(
				t("common.error"),
				t("editor.topViewWallArea.createDeductionModal.deductionExceedsTotalError")
			);
			return;
		}
		if (editingDeduction) {
			dispatch(topViewWallAreaActions.updateWallDeduction({
				id: measurement.id,
				deduction: {...deduction, id: editingDeduction.id},
			}));
		} else {
			dispatch(topViewWallAreaActions.addWallDeduction({id: measurement.id, deduction}));
		}
		closeModal();
	}

	function handleEditDeduction(deduction: WallDeduction) {
		setEditingDeduction(deduction);
		setModal(PanelItemModal.DEDUCTION);
	}

	function handleDeleteDeduction(deductionId: string) {
		dispatch(topViewWallAreaActions.deleteWallDeduction({id: measurement.id, deductionId}));
	}

	function addToPinnedItems() {
		pinnedItems.createPinnedMeasurement(
			measurement.material.id,
			measurement.material.type,
			MeasurementType.TOP_VIEW_WALL_AREA,
			measurement.material.name,
			undefined,
			undefined,
			measurement.style,
			!!(measurement.material && measurement.material.isAsset),
			measurement.material && measurement.material.assetTypeId ? measurement.material.assetTypeId : 0
		);
	}

	function handleSaveHeights(heights: Record<string, number | undefined>, defaultHeight: number | undefined) {
		for (const [pointId, height] of Object.entries(heights)) {
			if (height !== undefined) {
				dispatch(topViewWallAreaActions.setPointHeight({id: measurement.id, pointId, height}));
			} else {
				dispatch(topViewWallAreaActions.removePointHeight({id: measurement.id, pointId}));
			}
		}
		dispatch(topViewWallAreaActions.setDefaultHeight({id: measurement.id, defaultHeight}));
		setIsExpanded(true);
		closeModal();
	}

	function removeFromPinnedItems() {
		pinnedItems.deletePinnedMeasurement(pinnedMeasurement!.id);
	}

	const cls = [
		"panel-item",
		isActive && "is-active",
		isExpanded && "is-expanded",
	].filter(Boolean).join(" ");

	const panelItemBarCls = [
		"panel-item_bar",
		isItemHover && "is-hover",
	].filter(Boolean).join(" ");

	return (
		<>
			<MeasurementStyleSelectorModal
				isOpen={modal === PanelItemModal.STYLE}
				initialStyle={measurement.style}
				measurementType={EditorTool.TOP_VIEW_WALL_AREA}
				onSave={(style) => handleStyleSave(style as AreaStyle)}
				onClose={closeModal}
			/>
			<ConfirmationModal
				isOpen={modal === PanelItemModal.DELETE}
				text={t("editor.deleteItemConfirmationModalQuestion")}
				onConfirm={handleDelete}
				onClose={closeModal}
			/>
			<SetWallHeightsModal
				isOpen={modal === PanelItemModal.HEIGHTS}
				measurement={measurement}
				onSave={handleSaveHeights}
				onClose={closeModal}
			/>
			<CreateWallDeductionModal
				isOpen={modal === PanelItemModal.DEDUCTION}
				onSave={handleSaveDeduction}
				onClose={closeModal}
				existingDeduction={editingDeduction ?? undefined}
			/>
			<div className={cls}>
				<div
					className={panelItemBarCls}
					onMouseEnter={highlightPanelActions.setHighlight}
					onMouseLeave={highlightPanelActions.clearHighlight}
					onFocus={highlightPanelActions.setHighlight}
					onBlur={highlightPanelActions.clearHighlight}
				>
					<div className={"panel-item_bar_icon"} role="button" tabIndex={0}
						onClick={() => setIsExpanded(!isExpanded)}
						onKeyUp={(e) => e.key === "Enter" && setIsExpanded(!isExpanded)}>
						<IconArrowSmall isActive={isActive}/>
					</div>
					<div className={"panel-item_bar_content"} role="button" tabIndex={0}
						onClick={toggleActivate}
						onKeyUp={(e) => e.key === "Enter" && toggleActivate()}>
						<div className="panel-item_bar_content_label">
							{measurement.material.name}
						</div>
						{!!pinnedMeasurement && (
							<div className="panel-item_bar_content_pin-icon"><IconPin/></div>
						)}
						<div className="panel-item_bar_content_spacer"/>
					</div>
					<div className={"panel-item_bar_props"}>({formattedValue})</div>
					<div className={"panel-item_bar_actions"}>
						<IconVisibleButton onClick={toggleVisibility} isActive={isActive} visible={measurement.visible}/>
						<Dropdown disabled={editorLocked} isActive={isActive}>
							{isOneOff ? (
								<>
									<DropdownItem
										label={t("common.buttons.edit")}
										onClick={openChangeOneOffModal(t("projects.oneOffModal.title.editItem"))}
									/>
									<DropdownItem label={t("editor.convertToCatalogue")} onClick={openChangeMaterialModal}/>
								</>
							) : (
								<>
									<DropdownItem label={t("common.buttons.edit")} onClick={openStyleModal}/>
									<DropdownItem label={t("editor.changeMaterial")} onClick={openChangeMaterialModal}/>
									<DropdownItem
										label={t("editor.convertToOneOff")}
										onClick={openChangeOneOffModal(t("editor.convertToOneOff"))}
									/>
								</>
							)}
							<DropdownItem
								label={t("editor.area.createDeduction")}
								onClick={createDeduction}
							/>
							<DropdownItem label={t("editor.topViewWallArea.setHeights")} onClick={openHeightsModal}/>
							<DropdownItem label={t("common.buttons.delete")} onClick={openDeleteModal}/>
							{!!pinnedMeasurement ? (
								<DropdownItem
									label={t("editor.panel.pinnedItems.removeFromPinnedItems")}
									onClick={removeFromPinnedItems}
								/>
							) : (
								<DropdownItem
									label={t("editor.panel.pinnedItems.addToPinnedItems")}
									onClick={addToPinnedItems}
								/>
							)}
						</Dropdown>
					</div>
				</div>
				<div className="panel-item_content">
					{measurement.defaultHeight !== undefined && (
						<div style={{padding: "4px 12px", fontSize: 12, opacity: 0.7}}>
							{`${t("editor.topViewWallArea.setHeightsModal.defaultHeight")}: ${formatLength(measurement.defaultHeight, settings?.measurementSystem)}`}
						</div>
					)}
					{orderedPoints.map((point, index) => {
						const height = measurement.heightMap[point.id];
						if (height === undefined) return null;
						return (
							<WallHeightPointItem
								key={point.id}
								label={TopViewWallAreaHelper.getPointLabel(index)}
								value={height}
								disabled={editorLocked}
								onPropsClick={openHeightsModal}
								onDelete={() => dispatch(topViewWallAreaActions.removePointHeight({
									id: measurement.id,
									pointId: point.id,
								}))}
							/>
						);
					})}
					{measurement.wallDeductions.map(deduction => (
						<WallDeductionItem
							key={deduction.id}
							deduction={deduction}
							disabled={editorLocked}
							onEdit={() => handleEditDeduction(deduction)}
							onDelete={() => handleDeleteDeduction(deduction.id)}
						/>
					))}
				</div>
			</div>
		</>
	);
};

export default TopViewWallAreaPanelItem;
